import { useState, useRef, useEffect } from 'react';
import { Send, Mic, Square, Play, Pause, X, Trash2 } from 'lucide-react';

type TextMessage = {
  id: string;
  type: 'text';
  from: 'user' | 'firm';
  text: string;
  time: string;
  read?: boolean;
};

type VoiceMessage = {
  id: string;
  type: 'voice';
  from: 'user' | 'firm';
  duration: number; // seconds
  audioUrl?: string; // optional - mock messages have no real audio
  waveform: number[]; // 0..1 bar heights for the visualisation
  time: string;
  read?: boolean;
};

type Message = TextMessage | VoiceMessage;

// Generate a random waveform pattern for a given seed
const fakeWaveform = (count: number, seed = 1): number[] =>
  Array.from({ length: count }, (_, i) => {
    const v = Math.sin(i * 0.6 + seed) * 0.4 + Math.cos(i * 0.3 + seed * 2) * 0.3 + 0.5;
    return Math.max(0.15, Math.min(1, v));
  });

const seedMessages: Message[] = [
  {
    id: 'm1',
    type: 'text',
    from: 'firm',
    text: 'Morning Bashir - your Q3 BAS draft is ready for review when you have a moment.',
    time: 'Yesterday, 9:14 AM',
  },
  {
    id: 'm2',
    type: 'text',
    from: 'user',
    text: 'Thanks Hamid, will look this afternoon. One quick question…',
    time: 'Yesterday, 2:30 PM',
  },
  {
    id: 'm3',
    type: 'voice',
    from: 'user',
    duration: 18,
    waveform: fakeWaveform(28, 3),
    time: 'Yesterday, 2:31 PM',
  },
  {
    id: 'm4',
    type: 'voice',
    from: 'firm',
    duration: 42,
    waveform: fakeWaveform(32, 7),
    time: 'Yesterday, 4:08 PM',
  },
  {
    id: 'm5',
    type: 'text',
    from: 'firm',
    text: 'Also - equipment depreciation will save you about $1,180. Worth the receipts you sent through.',
    time: 'Yesterday, 4:10 PM',
  },
  {
    id: 'm6',
    type: 'text',
    from: 'user',
    text: 'Brilliant - thanks for the heads up.',
    time: 'Today, 8:42 AM',
  },
];

export default function MessagesPageV3() {
  const [messages, setMessages] = useState<Message[]>(seedMessages);
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingWaveform, setRecordingWaveform] = useState<number[]>([]);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderTimerRef = useRef<number | null>(null);
  const recordedBlobsRef = useRef<Blob[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const sendText = (text: string) => {
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    setMessages(prev => [...prev, {
      id: String(Date.now()),
      type: 'text',
      from: 'user',
      text: text.trim(),
      time: `Today, ${time}`,
    }]);
    setInput('');

    // Simulate firm reply after 1.5s
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: String(Date.now() + 1),
        type: 'text',
        from: 'firm',
        text: "Got it - I'll come back to you shortly.",
        time: `Today, ${new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}`,
      }]);
    }, 1500);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recordedBlobsRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedBlobsRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      recorderRef.current = recorder;
      setIsRecording(true);
      setRecordingDuration(0);
      setRecordingWaveform([]);

      // Tick the duration + push pseudo-waveform bars
      recorderTimerRef.current = window.setInterval(() => {
        setRecordingDuration(d => d + 0.1);
        setRecordingWaveform(w => [...w, Math.random() * 0.7 + 0.2].slice(-40));
      }, 100);
    } catch {
      // Mic blocked - fall back to a simulated 5-second recording so the
      // prototype still demonstrates the flow on devices without mic access.
      setIsRecording(true);
      setRecordingDuration(0);
      setRecordingWaveform([]);
      recorderTimerRef.current = window.setInterval(() => {
        setRecordingDuration(d => {
          if (d >= 5) { stopRecording(); return d; }
          return d + 0.1;
        });
        setRecordingWaveform(w => [...w, Math.random() * 0.7 + 0.2].slice(-40));
      }, 100);
    }
  };

  const stopRecording = () => {
    if (recorderTimerRef.current) {
      window.clearInterval(recorderTimerRef.current);
      recorderTimerRef.current = null;
    }
    let audioUrl: string | undefined;
    if (recorderRef.current) {
      try { recorderRef.current.stop(); } catch {}
      const blob = new Blob(recordedBlobsRef.current, { type: 'audio/webm' });
      if (blob.size > 0) audioUrl = URL.createObjectURL(blob);
      recorderRef.current = null;
    }
    const finalDuration = Math.max(1, Math.round(recordingDuration));
    const finalWaveform = recordingWaveform.length > 0 ? recordingWaveform : fakeWaveform(28, Date.now() % 100);
    const time = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    setMessages(prev => [...prev, {
      id: String(Date.now()),
      type: 'voice',
      from: 'user',
      duration: finalDuration,
      audioUrl,
      waveform: finalWaveform,
      time: `Today, ${time}`,
    }]);
    setIsRecording(false);
    setRecordingDuration(0);
    setRecordingWaveform([]);
  };

  const cancelRecording = () => {
    if (recorderTimerRef.current) {
      window.clearInterval(recorderTimerRef.current);
      recorderTimerRef.current = null;
    }
    if (recorderRef.current) {
      try { recorderRef.current.stop(); } catch {}
      recorderRef.current = null;
    }
    recordedBlobsRef.current = [];
    setIsRecording(false);
    setRecordingDuration(0);
    setRecordingWaveform([]);
  };

  return (
    <div className="pt-6">
      <div>
        <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">§ 05 · Messages</p>
        <h2 className="mt-2 font-serif text-3xl font-bold leading-tight tracking-tight">Direct Channel</h2>
        <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">With Marcus Bell · text or voice</p>
      </div>

      {/* Channel meta */}
      <div className="mt-4 flex items-center gap-3 border-y border-stone-300 dark:border-stone-700 py-3">
        <div className="flex h-9 w-9 items-center justify-center border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-[11px] font-bold text-stone-50 dark:text-stone-900">
          MB
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold">Marcus Bell</p>
          <p className="font-mono text-[11px] uppercase tracking-wider text-stone-500">
            <span className="inline-block h-1.5 w-1.5 align-middle bg-emerald-600 rounded-full mr-1.5" />
            Online · responds in &lt; 1h on weekdays
          </p>
        </div>
      </div>

      {/* Thread */}
      <div className="mt-4 space-y-4 pb-44">
        {messages.map((m, i) => {
          const showDateBreak = i === 0 || messages[i - 1].time.split(',')[0] !== m.time.split(',')[0];
          return (
            <div key={m.id}>
              {showDateBreak && (
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
                  <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500">
                    {m.time.split(',')[0]}
                  </span>
                  <div className="flex-1 h-px bg-stone-200 dark:bg-stone-800" />
                </div>
              )}
              {m.type === 'text' ? <TextBubble msg={m} /> : <VoiceBubble msg={m} />}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Fixed input bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-stone-300 dark:border-stone-700 bg-stone-50/95 dark:bg-stone-950/95 backdrop-blur-md">
        <div className="mx-auto w-full max-w-[680px] px-5 py-3">
          {isRecording ? (
            <RecordingBar
              duration={recordingDuration}
              waveform={recordingWaveform}
              onCancel={cancelRecording}
              onSend={stopRecording}
            />
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); sendText(input); }}
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 items-center gap-2 border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 p-1.5">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 bg-transparent px-2 py-2 text-sm text-stone-900 dark:text-stone-100 placeholder:text-stone-400 focus:outline-none"
                />
                {!input.trim() && (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="flex h-9 w-9 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-teal-700 dark:text-teal-400 hover:border-teal-700"
                    aria-label="Record voice message"
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                )}
              </div>
              {input.trim() && (
                <button
                  type="submit"
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-stone-50 dark:text-stone-900 hover:bg-stone-800"
                >
                  <Send className="h-4 w-4" />
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function TextBubble({ msg }: { msg: TextMessage }) {
  const isUser = msg.from === 'user';
  return (
    <div className={isUser ? 'flex justify-end' : 'flex items-start gap-2'}>
      {!isUser && (
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-[10px] font-bold tracking-widest text-stone-700 dark:text-stone-300">
          HV
        </div>
      )}
      <div className={`max-w-[78%] ${isUser ? '' : ''}`}>
        <div
          className={
            isUser
              ? 'border border-stone-300 dark:border-stone-700 bg-stone-200 dark:bg-stone-800 px-4 py-2.5 text-sm text-stone-900 dark:text-stone-100'
              : 'border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-4 py-2.5 text-sm text-stone-800 dark:text-stone-200'
          }
        >
          {msg.text}
        </div>
        <p className={`mt-1 font-mono text-[11px] uppercase tracking-wider text-stone-400 dark:text-stone-500 ${isUser ? 'text-right' : ''}`}>
          {msg.time.includes(',') ? msg.time.split(', ')[1] : msg.time}
        </p>
      </div>
    </div>
  );
}

function VoiceBubble({ msg }: { msg: VoiceMessage }) {
  const isUser = msg.from === 'user';
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const togglePlay = () => {
    // Real audio playback if we have a blob URL
    if (msg.audioUrl) {
      if (!audioRef.current) {
        audioRef.current = new Audio(msg.audioUrl);
        audioRef.current.onended = () => { setPlaying(false); setProgress(0); };
        audioRef.current.ontimeupdate = () => {
          if (!audioRef.current) return;
          setProgress(audioRef.current.currentTime / Math.max(0.1, audioRef.current.duration || msg.duration));
        };
      }
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play().catch(() => {});
        setPlaying(true);
      }
      return;
    }
    // Mock playback for seed messages - animate the progress bar over the duration
    if (playing) {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      setPlaying(false);
      return;
    }
    setPlaying(true);
    setProgress(0);
    const startedAt = Date.now();
    intervalRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const p = Math.min(1, elapsed / msg.duration);
      setProgress(p);
      if (p >= 1) {
        if (intervalRef.current) window.clearInterval(intervalRef.current);
        setPlaying(false);
        setProgress(0);
      }
    }, 50);
  };

  const fmtDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className={isUser ? 'flex justify-end' : 'flex items-start gap-2'}>
      {!isUser && (
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-[10px] font-bold tracking-widest text-stone-700 dark:text-stone-300">
          HV
        </div>
      )}
      <div className="max-w-[78%]">
        <div
          className={
            isUser
              ? 'flex items-center gap-3 border border-stone-300 dark:border-stone-700 bg-stone-200 dark:bg-stone-800 px-3 py-2.5 text-stone-900 dark:text-stone-100'
              : 'flex items-center gap-3 border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-3 py-2.5'
          }
        >
          <button
            onClick={togglePlay}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-teal-700 dark:bg-teal-500 text-white hover:opacity-90"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="ms-0.5 h-4 w-4" />}
          </button>
          <Waveform
            bars={msg.waveform}
            progress={progress}
            isUser={isUser}
          />
          <span className="flex-shrink-0 font-mono text-[11px] tabular-nums text-stone-500">
            {fmtDuration(msg.duration)}
          </span>
        </div>
        <p className={`mt-1 font-mono text-[11px] uppercase tracking-wider text-stone-400 dark:text-stone-500 ${isUser ? 'text-right' : ''}`}>
          {msg.time.includes(',') ? msg.time.split(', ')[1] : msg.time} · voice
        </p>
      </div>
    </div>
  );
}

function Waveform({ bars, progress }: { bars: number[]; progress: number; isUser?: boolean }) {
  return (
    <div className="flex h-8 flex-1 items-end gap-[2px]">
      {bars.map((h, i) => {
        const played = i / Math.max(1, bars.length - 1) <= progress;
        return (
          <span
            key={i}
            className={played ? 'bg-teal-700 dark:bg-teal-400' : 'bg-stone-400 dark:bg-stone-600'}
            style={{ height: `${Math.max(15, h * 100)}%`, width: 2.5 }}
          />
        );
      })}
    </div>
  );
}

function RecordingBar({
  duration, waveform, onCancel, onSend,
}: { duration: number; waveform: number[]; onCancel: () => void; onSend: () => void }) {
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };
  return (
    <div className="flex items-center gap-2 border border-rose-700/60 bg-rose-50 dark:bg-rose-950/30 p-1.5">
      <button
        type="button"
        onClick={onCancel}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:border-rose-700"
        aria-label="Cancel recording"
      >
        <Trash2 className="h-4 w-4" />
      </button>
      <div className="flex flex-1 items-center gap-2 px-2">
        <span className="flex h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-rose-600" />
        <span className="flex-shrink-0 font-mono text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400">
          REC {fmt(duration)}
        </span>
        <div className="flex h-6 flex-1 items-end gap-[2px] overflow-hidden">
          {waveform.slice(-30).map((h, i) => (
            <span
              key={i}
              className="bg-rose-600 dark:bg-rose-400"
              style={{ height: `${Math.max(15, h * 100)}%`, width: 2 }}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onSend}
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-stone-50 dark:text-stone-900 hover:bg-stone-800"
        aria-label="Stop and send"
      >
        <Square className="h-4 w-4 fill-current" />
      </button>
    </div>
  );
}
